// src/hooks/useReuniones.js
import { useState, useEffect } from "react";
import {
  collection, addDoc, deleteDoc, doc,
  onSnapshot, query, where, orderBy,
  serverTimestamp, updateDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { crearActividadCliente } from "./useActividadesCliente";
import { guardarInteraccion } from "./useMemoriaAgente";
import toast from "react-hot-toast";

const COL = "reuniones";

// Standalone — crea reunión, propaga proximoContacto al cliente y alimenta DIS
export async function crearReunion(datos, userId) {
  const ref = await addDoc(collection(db, COL), {
    ...datos,
    dolores:         datos.dolores         || [],
    objeciones:      datos.objeciones       || [],
    prioridades:     datos.prioridades      || [],
    proximasCompras: datos.proximasCompras  || [],
    probabilidadCierre: datos.probabilidadCierre || 0,
    creadoEn:  serverTimestamp(),
    creadoPor: auth.currentUser?.email || "",
  });

  // Propaga próximo contacto como fuente única de verdad en el cliente
  if (datos.clienteId && datos.proximoContacto) {
    await updateDoc(doc(db, "clientes", datos.clienteId), {
      proximoSeguimiento: datos.proximoContacto,
    });
  }

  // Registra como actividad en el perfil del cliente
  if (datos.clienteId) {
    await crearActividadCliente({
      clienteId:      datos.clienteId,
      clienteNombre:  datos.clienteNombre  || "",
      clienteEmpresa: datos.clienteEmpresa || "",
      responsable:    datos.responsable    || "",
      tipo:           datos.tipo           || "Reunión",
      fecha:          datos.fecha,
      descripcion:    datos.notas || `Reunión ${datos.tipo || ""} — ${datos.dolores?.join(", ") || "sin dolores"}`,
      resultado:      `Probabilidad cierre: ${datos.probabilidadCierre || 0}%`,
      proximoContacto: datos.proximoContacto || "",
    });
  }

  // Alimenta memoria del DIS con dolores y objeciones detectados
  if (userId && datos.clienteId && (datos.dolores?.length || datos.objeciones?.length)) {
    await guardarInteraccion({
      userId,
      clienteId:     datos.clienteId,
      clienteNombre: datos.clienteNombre || "",
      tipo:          "reunion",
      accion:        datos.tipo || "Reunión",
      nota: [
        datos.dolores?.length    ? `Dolores: ${datos.dolores.join(", ")}`       : "",
        datos.objeciones?.length ? `Objeciones: ${datos.objeciones.join(", ")}` : "",
        datos.prioridades?.length? `Prioridades: ${datos.prioridades.join(", ")}`: "",
      ].filter(Boolean).join(" | "),
      dias: datos.proximoContacto ? 7 : 0,
    });
  }

  return ref.id;
}

// Hook — todas las reuniones (para página Reuniones)
export function useReuniones() {
  const [reuniones, setReuniones] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const q = query(collection(db, COL), orderBy("fecha", "desc"));
    const unsub = onSnapshot(q, snap => {
      setReuniones(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => {
      // Fallback sin índice
      const q2 = query(collection(db, COL));
      const unsub2 = onSnapshot(q2, snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
        setReuniones(list);
        setLoading(false);
      });
      return unsub2;
    });
    return unsub;
  }, []);

  const actualizar = async (id, datos) => {
    try {
      await updateDoc(doc(db, COL, id), datos);
      toast.success("✅ Reunión actualizada");
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const eliminar = async (id) => {
    try {
      await deleteDoc(doc(db, COL, id));
      toast.success("Reunión eliminada");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return { reuniones, loading, actualizar, eliminar };
}

// Hook — reuniones de UN cliente específico (para PerfilCliente)
export function useReunionesCliente(clienteId) {
  const [reuniones, setReuniones] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    if (!clienteId) { setReuniones([]); setLoading(false); return; }
    const q = query(
      collection(db, COL),
      where("clienteId", "==", clienteId),
      orderBy("fecha", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setReuniones(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => {
      const q2 = query(collection(db, COL), where("clienteId", "==", clienteId));
      const unsub2 = onSnapshot(q2, snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
        setReuniones(list);
        setLoading(false);
      });
      return unsub2;
    });
    return unsub;
  }, [clienteId]);

  return { reuniones, loading };
}
